/**
 * Бюджет печати путевого листа: одна лестница сроков на все четыре слоя (ADR 0148).
 *
 * Печать — самая долгая синхронная операция портала: сервер собирает бланки, переводит их в PDF
 * внешним конвертером (`services/office-pdf.ts`) и склеивает в один документ, держа всё это время
 * HTTP-соединение. Сроков на её пути ровно четыре, и каждый живёт в своём слое: конвертер, ручка
 * API, `proxy_read_timeout` у обоих nginx и ожидание вкладки.
 *
 * **Почему числа собраны здесь, а не там, где применяются.** Ровно потому, что однажды они
 * разъехались и это стоило рабочего дня: конвертер получал `30 с × число листов` (на полусотне —
 * двадцать пять минут), nginx рвал соединение через минуту, а вкладка своего срока не имела вовсе.
 * Человек видел обрыв, повторял печать — и каждая брошенная попытка продолжала жить на сервере,
 * занимая ядро и соединение пула. Ни один из слоёв при этом не был неправ поодиночке; неправым
 * было их сочетание, а сочетания не видно, пока числа лежат в четырёх файлах трёх языков.
 *
 * **Правило лестницы: срок тем короче, чем ближе слой к работе.** Первым обязан сдаться тот, кто
 * знает, что именно не получилось, — конвертер: он отвечает «бланк не готов», и это осмысленный
 * ответ. Последним — вкладка. Обратный порядок и есть тот самый дефект: раньше всех сдавался
 * nginx, а он про печать не знает ничего и умеет только оборвать. Инвариант проверяется тестом
 * (`apps/api/test/print-budget.test.ts`), он же сверяет числа с конфигами nginx: файлы деплоя
 * читать типами нельзя, а разъезжаются они молча.
 *
 * **Откуда числа.** Замеры на восьмиядерной машине: один лист — 1.1 с, десять — 3.2 с, полсотни на
 * одном ядре — 12.4 с, склейка полусотни — 0.12 с. Прод делит VPS с соседними порталами, поэтому
 * в бюджет заложен примерно десятикратный запас к замеру: срок здесь — не ожидаемое время, а
 * граница, за которой ожидание бессмысленно.
 */

/**
 * Запуск конвертера. Стоит дороже самой конвертации: LibreOffice поднимает офисный пакет с нуля,
 * и на пустой машине это около секунды. Пятнадцать — с запасом на холодный кеш и занятый диск.
 */
const CONVERTER_STARTUP_MS = 15_000;

/**
 * Прибавка на каждый лист сверх запуска. Замер даёт четверть секунды на лист под полной
 * загрузкой ядра; два — тот же порядок запаса, что и у прочих чисел лестницы.
 */
const CONVERTER_PER_SHEET_MS = 2_000;

/**
 * Потолок бюджета конвертера. Он и есть главная поправка к прежнему устройству: срок растёт с
 * числом листов, но не безгранично — иначе большая пачка снова получила бы право занимать сервер
 * дольше, чем его готов ждать кто бы то ни было.
 */
const CONVERTER_MAX_MS = 120_000;

/**
 * Весь запрос печати целиком: сборка бланков из базы, конвертация, склейка, аудит. Больше
 * конвертерного потолка ровно настолько, чтобы на остальное хватило даже при вялой базе.
 */
const HANDLER_MS = 150_000;

/**
 * `proxy_read_timeout` обоих nginx на путях печати — внешнего (`deploy/nginx/technic.conf`) и того,
 * что внутри контейнера web (`deploy/nginx/spa.conf`).
 *
 * Только на путях печати: общий потолок для `/api/` остаётся коротким намеренно. Обычная ручка,
 * висящая две минуты, — это поломка, и обрывать её надо быстро; печать же — единственное место,
 * где столько ждать нормально.
 */
const PROXY_READ_MS = 165_000;

/**
 * Сколько ждёт вкладка. Верхняя ступень лестницы и единственная, которую видит человек: до неё
 * дело доходит, только если промолчали все, кто ниже, — то есть при поломке, а не при большой
 * пачке.
 */
const CLIENT_MS = 180_000;

/**
 * Лестница целиком — в порядке убывания срока. Читатели берут числа отсюда, а не объявляют свои:
 * ровно это и защищает от повторения дефекта.
 */
export const PRINT_BUDGET = {
  converterStartupMs: CONVERTER_STARTUP_MS,
  converterPerSheetMs: CONVERTER_PER_SHEET_MS,
  converterMaxMs: CONVERTER_MAX_MS,
  handlerMs: HANDLER_MS,
  proxyReadMs: PROXY_READ_MS,
  clientMs: CLIENT_MS,
} as const;

/**
 * Ступени лестницы снизу вверх — для проверки инварианта и для сообщений об ошибке.
 *
 * Порядок именно такой: `converterMax` ≤ `handler` ≤ `proxyRead` ≤ `clientMs`. Равенство соседних
 * ступеней запрещено — совпавшие сроки означают гонку, в которой неизвестно, кто ответит первым, а
 * весь смысл лестницы в том, чтобы это было известно заранее.
 */
export const PRINT_BUDGET_LADDER = [
  { name: 'converterMaxMs', ms: CONVERTER_MAX_MS, where: 'конвертер (services/office-pdf.ts)' },
  { name: 'handlerMs', ms: HANDLER_MS, where: 'ручка печати (routes/waybills.ts)' },
  { name: 'proxyReadMs', ms: PROXY_READ_MS, where: 'proxy_read_timeout обоих nginx' },
  { name: 'clientMs', ms: CLIENT_MS, where: 'ожидание вкладки (WaybillPrint.tsx)' },
] as const;

/**
 * Срок конвертера под конкретную пачку: запуск плюс лист, но не выше потолка.
 *
 * Пропорциональность здесь не ради точности, а ради разговора с человеком. Одиночный лист, не
 * уложившийся в семнадцать секунд, — это поломка, и сказать о ней надо сразу, а не заставлять
 * ждать две минуты «на всякий случай». Пачка же в полсотни листов честно работает десятки секунд,
 * и обрывать её на семнадцатой секунде значило бы объявить поломкой нормальную работу.
 */
export function converterBudgetMs(sheets: number): number {
  const wanted = CONVERTER_STARTUP_MS + CONVERTER_PER_SHEET_MS * Math.max(1, sheets);
  return Math.min(wanted, CONVERTER_MAX_MS);
}

/**
 * Часть пачки, которую стоит попробовать вместо целой.
 *
 * Считается от неудавшегося числа, а не от потолка бюджета, и вот почему. Потолок отмерен с
 * запасом под предельные полсотни листов — если пачка в него не уложилась, значит машина медленнее
 * расчётной, и совет «возьмите столько, сколько теоретически влезает» повторил бы ту же неудачу.
 * Треть — это не расчёт, а осмысленный шаг назад: он заведомо меньше того, что уже не получилось.
 */
export function suggestedPrintChunk(sheets: number): number {
  return Math.max(5, Math.floor(sheets / 3));
}

/**
 * Код отказа по сроку — свой, а не общий `version_conflict`.
 *
 * Различать их приходится по той же причине, по которой заведён `waybill_ack_required`: исход у
 * человека разный. `version_conflict` портал показывает как «откройте заново», и для не успевшей
 * печати это ложный совет — открывать заново нечего, надо либо повторить, либо разбить пачку.
 * Прежде этот отказ приходил именно под общим кодом, и сообщение «выгрузите файлом» было
 * единственным, что отличало его от конфликта версий.
 */
export const PRINT_TIMEOUT_CODE = 'print_timeout';

/**
 * Текст отказа по сроку. Составляет его сервер, а живёт он здесь — рядом с лестницей, которая этот
 * отказ и порождает: совет в тексте прямо зависит от чисел выше, и разъехаться с ними он не должен.
 */
export function printTimeoutMessage(sheets: number): string {
  if (sheets <= 1) {
    return 'Бланк не удалось подготовить к печати за отведённое время. Повторите попытку, а если она повторится — выгрузите лист файлом и напечатайте из редактора';
  }
  return `Пачку из ${sheets} л. не удалось подготовить за отведённое время. Напечатайте её частями — примерно по ${suggestedPrintChunk(sheets)} л.`;
}
